import { useEffect } from 'react';

import { requestSubscription } from 'react-relay';

import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';

import type { useAgentSessionsLiveUpdatesSubscription as Subscription } from './__generated__/useAgentSessionsLiveUpdatesSubscription.graphql';
import { agentSessionsLiveCoordinator } from './agentSessionsLiveCoordinator';
import { getAgentSessionsLiveEvent } from './getAgentSessionsLiveEvent';
import { isSessionInLoadedConnection } from './isSessionInLoadedConnection';
import { resolveAgentSessionsCloudId } from './resolveAgentSessionsCloudId';
import { subscribeToHomeAgentSessions } from './subscribeToHomeAgentSessions';
import type { AgentSessionsScope } from './types';
import { useAgentSessionsLiveUpdatesSubscription } from './useAgentSessionsLiveUpdatesSubscription';
import type { AgentSessionsData } from './useLegacyAgentSessionsData';

const reportSubscriptionError = (exception: unknown): void => {
	void logException({ name: 'agentSessionsLiveSubscription', exception });
};

/** Resolve subscription scope without suspending the list or changing the public hook. */
export const useAgentSessionsLiveUpdates = ({
	options,
	data,
}: Readonly<{ options: AgentSessionsScope; data: AgentSessionsData }>): void => {
	const { scope, liveData } = data;
	const activationOnly = !options.cloudId?.trim() && Boolean(options.rovoActivationId?.trim());
	useEffect(() => {
		if (
			scope == null ||
			liveData == null ||
			liveData.environment.isServer() ||
			typeof window === 'undefined' ||
			typeof document === 'undefined'
		)
			return;

		let active = true;
		let unsubscribe: (() => void) | undefined;
		const connect = (cloudId?: string) => {
			if (!active || !cloudId || unsubscribe != null) return;
			const variables = {
				cloudId,
				accountId: scope.accountId,
				workspaceAri: scope.workspaceAri,
			};
			unsubscribe = agentSessionsLiveCoordinator.observe(liveData.reader, (onEvent) => {
				let payload: Subscription['response'] | null | undefined;
				const publish = (event: Parameters<typeof onEvent>[0], shouldInvalidate: boolean) =>
					onEvent(
						event.deleted
							? { ...event, accountId: scope.accountId, workspaceAri: scope.workspaceAri }
							: event,
						shouldInvalidate,
					);
				return (
					subscribeToHomeAgentSessions(
						variables,
						scope.rovoActivationId,
						publish,
						reportSubscriptionError,
					) ??
					requestSubscription<Subscription>(liveData.environment, {
						subscription: useAgentSessionsLiveUpdatesSubscription,
						variables,
						updater: (_store, data) => {
							payload = data;
						},
						onNext: () => {
							const response = payload;
							payload = undefined;
							const session = response?.convoai_onInboxSessionChanged?.session;
							const event = getAgentSessionsLiveEvent(response);
							if (event == null) return;
							publish(
								event,
								session == null ||
									event.conversationId == null ||
									!isSessionInLoadedConnection(liveData.reader, session.id),
							);
						},
						// Transport reconnect/replay semantics need separate backend verification.
						onError: reportSubscriptionError,
						onCompleted: () => {},
					})
				);
			});
		};
		// Activation-only callers need a cloud ID for the subscription transport.
		const lookup = activationOnly
			? resolveAgentSessionsCloudId(liveData.environment, {
					rovoActivationId: scope.rovoActivationId,
					onComplete: ({ cloudId }) => connect(cloudId),
				})
			: undefined;
		if (!activationOnly) connect(scope.cloudId);
		return () => {
			active = false;
			lookup?.dispose();
			unsubscribe?.();
		};
	}, [activationOnly, liveData, scope]);
};
